import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { ApiError, api } from "./api/client.js";
import { CompanyPage } from "./routes/CompanyPage.js";
import { DocumentPage } from "./routes/DocumentPage.js";
import { EmptyPane } from "./routes/EmptyPane.js";
import { QueuePage } from "./routes/QueuePage.js";
import { JobHistoryPage } from "./routes/JobHistoryPage.js";
import { NotificationsPage } from "./routes/NotificationsPage.js";
import { WarningsPage } from "./routes/WarningsPage.js";
import { ReviewPage } from "./routes/ReviewPage.js";
import { OpsPage } from "./routes/OpsPage.js";
import { RadarLayout } from "./routes/RadarLayout.js";
import { SignInPage } from "./routes/SignInPage.js";
import { SituationPage } from "./routes/SituationPage.js";
import { TablePage } from "./routes/TablePage.js";
import { SearchPage } from "./routes/SearchPage.js";

/**
 * The application root: the sign-in page when the owner is not signed in, an error when the
 * session check fails, and otherwise every route under the radar layout.
 */
export function App() {
  const [params] = useSearchParams();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    // A 401 is the answer, not a failure — retrying it just delays the sign-in screen.
    retry: (_count, error) => !(error instanceof ApiError && error.isUnauthenticated),
    staleTime: 5 * 60_000,
  });

  if (me.isLoading) return <div className="signin" aria-busy />;

  if (me.isError) {
    const err = me.error as Error;
    if (err instanceof ApiError && err.isUnauthenticated) {
      // `?error=unauthorized` is what every OAuth failure redirects to. The reasons
      // are deliberately not distinguished, so neither is the message.
      return (
        <SignInPage
          error={
            params.get("error") === "unauthorized"
              ? "That account is not allowed to use this instance."
              : null
          }
        />
      );
    }
    return (
      <div className="signin">
        <h1>Mosaic</h1>
        <p className="err">{err.message}</p>
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<RadarLayout />}>
        <Route path="/" element={<EmptyPane />} />
        <Route path="/situations/:id" element={<SituationPage />} />
        <Route path="/companies/:cik" element={<CompanyPage />} />
        <Route path="/filings/:accession" element={<DocumentPage />} />
        <Route path="/table" element={<TablePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/ops" element={<OpsPage />} />
        <Route path="/ops/review" element={<ReviewPage />} />
        <Route path="/ops/queue" element={<QueuePage />} />
        <Route path="/ops/history" element={<JobHistoryPage />} />
        <Route path="/ops/notifications" element={<NotificationsPage />} />
        <Route path="/ops/warnings" element={<WarningsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
